import type { DriveStep } from "driver.js";
import { ONBOARDING_IDS } from "../constants";

export function getPlayerProfileSteps(isMobile: boolean): DriveStep[] {
    return [
        {
            element: `#${ONBOARDING_IDS.PLAYER_HEADER}`,
            popover: {
                title: "Actions Bar",
                description:
                    "Switch tournaments to see how this player performed in different competitions. " +
                    "Generate an AI summary for a quick performance narrative, or export the full analysis to Excel.",
                side: "bottom",
                align: isMobile ? "center" : "start",
            },
        },
        {
            element: `#${ONBOARDING_IDS.PLAYER_HERO}`,
            popover: {
                title: "Player Profile",
                description:
                    "The banner shows the player's photo, full name, and position badge. " +
                    "Their most recently played match is shown with opponent and date — giving you immediate context before diving into the performance data below.",
                side: "bottom",
                align: "center",
            },
        },
        {
            element: `#${ONBOARDING_IDS.PLAYER_SKILLS}`,
            popover: {
                title: "Key Stats & Skill Radar",
                description:
                    "Three stat cards show the key numbers — Average KPI for the tournament, Best single-match KPI with the opponent it was scored against, and total Matches Played with win/loss record. " +
                    "The Skill Radar maps performance across all KPI dimensions as percentiles — wedges reaching the outer edge indicate strong areas, while flat sides show areas to develop. " +
                    "The Lead Scout Commentary provides an AI-generated narrative of this player's recent form.",
                side: "top",
                align: "center",
            },
        },
        {
            element: `#${ONBOARDING_IDS.PLAYER_RINK_MAP}`,
            popover: {
                title: "Rink Position Map",
                description:
                    "A heat map of where this player is most active on the ice. " +
                    "Hot zones highlight their dominant areas — useful for understanding their positioning role and tendencies in matches.",
                side: isMobile ? "bottom" : "right",
                align: "center",
            },
        },
        {
            element: `#${ONBOARDING_IDS.PLAYER_KPI_SELECTOR}`,
            onHighlightStarted: (element) => {
                if (!element) return;
                const container = document.querySelector<HTMLElement>(
                    ".flex-1.min-h-0.overflow-auto, main.overflow-auto"
                );
                if (!container) return;
                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const offset =
                    elementRect.top -
                    containerRect.top +
                    container.scrollTop -
                    containerRect.height / 2 +
                    elementRect.height / 2;
                container.scrollTo({ top: offset, behavior: "smooth" });
            },
            popover: {
                title: "KPI Category Picker",
                description:
                    "Click these tabs to switch between KPI categories — Total, Offensive, Defensive, Team Play, Effort, and Bonus. " +
                    "This controls both the Performance Over Time trend chart and the Underlying Data breakdown below it, " +
                    "so you can drill into any category from the high-level trend all the way down to individual contributing actions.",
                side: "bottom",
                align: "center",
            },
        },
        {
            element: `#${ONBOARDING_IDS.PLAYER_KPI_CHART}`,
            popover: {
                title: "Performance Over Time",
                description:
                    "Tracks the selected KPI across every match in the tournament. " +
                    "Use it to spot consistency, upward momentum, or dips after specific games. " +
                    "The trend line shows the overall direction across the season.",
                side: isMobile ? "top" : "bottom",
                align: "center",
            },
        },
        {
            element: `#${ONBOARDING_IDS.PLAYER_KPI_DETAILS}`,
            popover: {
                title: "Underlying Data Contributing",
                description:
                    "Shows the raw actions that make up the selected KPI category. " +
                    "Each bar breaks down what the player actually did per match — hover to see exact values " +
                    "and pinpoint which specific actions drove a high or low score.",
                side: "top",
                align: "center",
            },
        },
        {
            element: `#${ONBOARDING_IDS.PLAYER_ACHIEVEMENTS}`,
            popover: {
                title: "Badges & Achievements",
                description:
                    "Earned through exceptional performance — scoring streaks, defensive shutouts, effort milestones, and more. " +
                    "These highlight standout plays and reward consistency throughout the season.",
                side: "top",
                align: "center",
            },
        },
    ];
}

export const playerProfileSteps: DriveStep[] = getPlayerProfileSteps(false);
